/**
 * HTTP transport for real API calls to `gi-api-server`.
 *
 * Concerns this module owns:
 *   1. Compose a final URL from `VITE_API_BASE_URL` + the server's
 *      `/api/v1/` path prefix. Callers pass bare paths
 *      ("/gi", "/consent/:id"), the client prepends the prefix.
 *   2. Attach auth headers (see "Auth Headers" below).
 *   3. Sign each mutating request under a per-browser operator
 *      keypair, matching the axiom-client wire format defined in
 *      `packages/gi-sdk-ts/src/auth.ts` (integration spec §4.2).
 *   4. Parse RFC 7807 `application/problem+json` error envelopes
 *      into a typed `ApiHttpError` the UI can show cleanly.
 *
 * ─── Auth Headers ────────────────────────────────────────────────
 *
 *   Authorization:     `Bearer <api_key>`
 *                      Sent on every request.
 *
 *   X-AXIOM-TIMESTAMP: ISO 8601 UTC with millisecond precision
 *                      (`YYYY-MM-DDTHH:mm:ss.sssZ`). Sent on every
 *                      mutating request (POST / PUT / PATCH / DELETE).
 *
 *   X-AXIOM-NONCE:     16 random bytes, lowercase hex (32 chars).
 *                      Sent on every mutating request.
 *
 *   X-AXIOM-SIGNATURE: Ed25519 signature of the string-to-sign,
 *                      base64-encoded. Sent on every mutating request.
 *
 * ─── String-to-sign ──────────────────────────────────────────────
 *
 *   METHOD\n                   (GET / POST / PUT / PATCH / DELETE)
 *   PATH\n                     (path + query, e.g. `/api/v1/gi`)
 *   TIMESTAMP\n                (verbatim X-AXIOM-TIMESTAMP value)
 *   NONCE\n                    (verbatim X-AXIOM-NONCE value)
 *   sha256(body)               (lowercase hex; empty body → sha256
 *                               of the empty byte string)
 *
 *   The body on the wire is JCS-canonicalised JSON (RFC 8785) —
 *   the exact bytes sha256 runs over and the exact bytes posted.
 *
 * What this module deliberately does NOT do:
 *   - Retries (the engine is authoritative for duplicate-detection;
 *     a blind retry could double-submit).
 *   - Response caching (TanStack Query owns that one layer up).
 *   - WASM upload (Spark's `createGi` signs + posts the record
 *     client-side; WASM bytes never leave the browser in Phase 4.1).
 */

import { ed25519 } from '@noble/curves/ed25519';
import { sha256 } from '@noble/hashes/sha2';
import { bytesToHex } from '@noble/hashes/utils';

import { canonicalJsonStringify } from './mock/sign';
import { getAccessToken } from './supabase';

const BASE_URL = normaliseBaseUrl(
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3000',
);
const API_PATH_PREFIX = '/api/v1';

/** Fallback bearer used when no Supabase session is available
 *  (anonymous public-route access, dev environments without Supabase
 *  configured). The production middleware rejects this on auth-
 *  required routes; public routes (/health, /metrics) accept it. */
const DEV_BEARER =
  (import.meta.env.VITE_API_BEARER_TOKEN as string | undefined) ?? 'dev-operator-token';

const OPERATOR_KEY_STORAGE = 'gi-engine.operator-key.v1';

export class ApiHttpError extends Error {
  /** HTTP status code of the failed response. */
  readonly status: number;
  /** Short machine-readable error slug — derived from the `type`
   *  URI's last segment when the server returns RFC 7807. */
  readonly code: string;
  /** Human-readable summary (`title` field of the 7807 body). */
  readonly title: string;
  /** Longer detail (`detail` field of the 7807 body), if present. */
  readonly detail: string | undefined;

  constructor(params: {
    status: number;
    code: string;
    title: string;
    detail?: string | undefined;
  }) {
    const msg = params.detail ? `${params.title}: ${params.detail}` : params.title;
    super(msg);
    this.name = 'ApiHttpError';
    this.status = params.status;
    this.code = params.code;
    this.title = params.title;
    this.detail = params.detail;
  }

  /** True if the server indicated the resource was not found. */
  get notFound(): boolean {
    return this.status === 404;
  }
}

/** Fetch wrapper: prepends the API prefix, adds auth headers, signs
 *  mutating bodies, parses 7807 errors. Returns the parsed JSON
 *  body on success, or `null` for an HTTP 204. */
export async function apiFetch<T>(
  path: string,
  init: {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: unknown;
    headers?: Record<string, string>;
  } = {},
): Promise<T> {
  const method = init.method ?? 'GET';
  const pathWithQuery = `${API_PATH_PREFIX}${path}`;
  const url = `${BASE_URL}${pathWithQuery}`;

  const bodyBytes = bodyAsCanonicalBytes(init.body);
  const builtHeaders = await buildHeaders(method, pathWithQuery, bodyBytes);
  const headers = init.headers
    ? { ...builtHeaders as Record<string, string>, ...init.headers }
    : builtHeaders;

  const requestInit: RequestInit = { method, headers };
  if (method !== 'GET') {
    // Build a fresh buffer slice to satisfy DOM `BodyInit` — the
    // runtime accepts `Uint8Array`, but `lib.dom`'s type surface
    // on this Node/TS combination is narrower.
    requestInit.body = bodyBytes.buffer.slice(
      bodyBytes.byteOffset,
      bodyBytes.byteOffset + bodyBytes.byteLength,
    ) as ArrayBuffer;
  }
  const response = await fetch(url, requestInit);

  if (response.status === 204) {
    return undefined as T;
  }

  const raw = await response.text();

  if (!response.ok) {
    throw parseProblemResponse(response.status, response.headers, raw);
  }

  if (!raw) {
    return undefined as T;
  }

  try {
    return JSON.parse(raw) as T;
  } catch (err) {
    throw new ApiHttpError({
      status: response.status,
      code: 'malformed-response',
      title: 'Malformed server response',
      detail: `Could not parse JSON body (${(err as Error).message}).`,
    });
  }
}

/** Canonical JCS bytes of a request body, or an empty array for
 *  GETs / empty bodies. The same bytes become the signing input. */
function bodyAsCanonicalBytes(body: unknown): Uint8Array {
  if (body === undefined || body === null) return new Uint8Array();
  const json = canonicalJsonStringify(body);
  return new TextEncoder().encode(json);
}

async function buildHeaders(
  method: string,
  pathWithQuery: string,
  bodyBytes: Uint8Array,
): Promise<HeadersInit> {
  // Authorization: prefer the live Supabase JWT (auto-refreshed via
  // `getAccessToken`); fall back to the dev bearer when Supabase
  // isn't configured or no session exists. The server's middleware
  // accepts both — JWT verification first, ax_*/dev-fallback second.
  const supabaseJwt = await getAccessToken();
  const bearer = supabaseJwt ?? DEV_BEARER;
  const headers: Record<string, string> = {
    Authorization: `Bearer ${bearer}`,
    Accept: 'application/json, application/problem+json',
  };
  if (bodyBytes.byteLength > 0) {
    headers['Content-Type'] = 'application/json';
  }
  if (method !== 'GET') {
    const timestamp = new Date().toISOString();
    const nonceBytes = new Uint8Array(16);
    crypto.getRandomValues(nonceBytes);
    const nonceHex = bytesToHex(nonceBytes);
    const bodyHashHex = bytesToHex(sha256(bodyBytes));
    const stringToSign =
      `${method}\n` +
      `${pathWithQuery}\n` +
      `${timestamp}\n` +
      `${nonceHex}\n` +
      `${bodyHashHex}`;
    const kp = getOperatorKey();
    const sig = ed25519.sign(new TextEncoder().encode(stringToSign), kp.secret_key);
    headers['X-AXIOM-TIMESTAMP'] = timestamp;
    headers['X-AXIOM-NONCE'] = nonceHex;
    // Wire format MUST match the server's `verify_ed25519_signature`
    // parser (crates/gi-api-server/src/auth.rs): `ed25519:<sig_b64>:<pubkey_b64>`.
    // Sending only the bare base64 signature gets rejected at
    // `splitn(3, ':')` with parts.len() != 3 → 401 on every mutating
    // request when AUTH_STRICT=true. The server uses the embedded
    // pubkey to verify; it is intentionally per-browser ephemeral
    // (loaded from localStorage by `getOperatorKey`).
    headers['X-AXIOM-SIGNATURE'] = `ed25519:${base64(sig)}:${base64(kp.public_key)}`;
  }
  return headers;
}

function parseProblemResponse(
  status: number,
  headers: Headers,
  raw: string,
): ApiHttpError {
  const ct = headers.get('content-type') ?? '';
  if (ct.includes('application/problem+json') || ct.includes('application/json')) {
    try {
      const parsed = JSON.parse(raw) as {
        type?: string;
        title?: string;
        detail?: string;
        status?: number;
      };
      return new ApiHttpError({
        status,
        code: typeSlug(parsed.type) ?? statusSlug(status),
        title: parsed.title ?? statusSlug(status),
        detail: parsed.detail,
      });
    } catch {
      /* fall through to plain-text */
    }
  }
  return new ApiHttpError({
    status,
    code: statusSlug(status),
    title: statusSlug(status),
    detail: raw.length > 0 && raw.length < 512 ? raw : undefined,
  });
}

function typeSlug(typeUri: string | undefined): string | undefined {
  if (!typeUri) return undefined;
  const tail = typeUri.split(/[/#]/).filter(Boolean).pop();
  return tail?.toLowerCase();
}

function statusSlug(status: number): string {
  switch (status) {
    case 400:
      return 'bad-request';
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    case 404:
      return 'not-found';
    case 409:
      return 'conflict';
    case 422:
      return 'verification-failed';
    case 429:
      return 'too-many-requests';
    default:
      return status >= 500 ? 'server-error' : 'http-error';
  }
}

function normaliseBaseUrl(raw: string): string {
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
}

// ─── Operator key management ───────────────────────────────────────
//
// One Ed25519 key per browser, cached in localStorage. Used to sign
// every mutating request — the server's auth middleware verifies the
// signature against the embedded pubkey when AUTH_STRICT=true. The
// pubkey is sent on the wire as part of `X-AXIOM-SIGNATURE`; the
// secret key never leaves the browser.

interface OperatorKey {
  public_key: Uint8Array;
  secret_key: Uint8Array;
}

function getOperatorKey(): OperatorKey {
  const cached = loadOperatorKey();
  if (cached) return cached;
  const secret_key = ed25519.utils.randomPrivateKey();
  const public_key = ed25519.getPublicKey(secret_key);
  const kp: OperatorKey = { public_key, secret_key };
  saveOperatorKey(kp);
  return kp;
}

function loadOperatorKey(): OperatorKey | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(OPERATOR_KEY_STORAGE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { secret_hex: string; public_hex: string };
    return {
      secret_key: hexToBytes(parsed.secret_hex),
      public_key: hexToBytes(parsed.public_hex),
    };
  } catch {
    return null;
  }
}

function saveOperatorKey(kp: OperatorKey): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(
    OPERATOR_KEY_STORAGE,
    JSON.stringify({
      secret_hex: bytesToHex(kp.secret_key),
      public_hex: bytesToHex(kp.public_key),
    }),
  );
}

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

function base64(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

/** Base URL + API prefix exposed for diagnostic UIs ("connected to
 *  http://localhost:3000/api/v1"). */
export function apiBaseUrl(): string {
  return `${BASE_URL}${API_PATH_PREFIX}`;
}

/** Probe `GET /health`. Returns `true` iff the server responds 200. */
export async function probeHealth(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE_URL}${API_PATH_PREFIX}/health`);
    return res.ok;
  } catch {
    return false;
  }
}
